import {EventEmitter, Injectable, signal, WritableSignal} from "@angular/core";
import {ClientSettingsModel, NotificationClientSettings, ThemeMode, UsersSorting} from "../../../shared/client.settings";
import {UserSettingsNamespace} from "../../../shared/commands";
import {RemoteCommandService} from "./remote.command.service";
import {AllowPrivateMessages, ClientUser} from "../../../shared/user";
import {Subject} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {isInBrowser} from "../utils/clientutils";

@Injectable({
    providedIn: 'root'
})
export class UserSettingsService {
    private static KEY_SETTINGS = 'settings';

    private _settings: WritableSignal<ClientSettingsModel>;
    private _showExternalImages = signal(false);
    private _hideExternalImagesWarning = signal(this.readSessionStorageBool('hide_external_images_warning'));
    private _saveSubject = new Subject<void>();
    private _me: ClientUser;

    public themeChanged = new EventEmitter<ThemeMode>()

    constructor(private _remoteCommandService: RemoteCommandService) {
        this._settings = signal(this.defaultSettings());

        // Дебаунс сохранения для предотвращения спама при быстрых изменениях
        this._saveSubject.pipe(debounceTime(500)).subscribe(() => {
            this.executeSave();
        });

        this.readSettings(null)
    }

    onUserConnected(me: ClientUser, settings: ClientSettingsModel){
        this._me = me
        this.readSettings(settings)
    }

    readSettings(serverSettings: ClientSettingsModel) {
        let loadedSettings: Partial<ClientSettingsModel> = {}

        if (this._me?.registered && serverSettings) {
            loadedSettings = serverSettings
        } else if (isInBrowser()) {
            const raw = window.localStorage.getItem(UserSettingsService.KEY_SETTINGS)
            if (raw) {
                try {
                    loadedSettings = JSON.parse(raw)
                } catch (e) {
                    console.error('Failed to parse settings', e)
                }
            }
        }

        const merged = { ...this.defaultSettings(), ...loadedSettings }
        this._settings.set(merged)

        // Оповещаем о теме (важно для ThemeService)
        this.themeChanged.emit(merged.themeMode)
    }

    get allowPrivateMessages(): AllowPrivateMessages {
        return this._settings().allowPrivateMessages;
    }

    set allowPrivateMessages(value: AllowPrivateMessages) {
        this.updateSettings({ allowPrivateMessages: value });
    }

    get showPictures(): boolean {
        return this._settings().pictures;
    }

    set showPictures(value: boolean) {
        this.updateSettings({ pictures: value });
    }

    get showServiceMessages(): boolean {
        return this._settings().serviceMessages;
    }

    set showServiceMessages(value: boolean) {
        this.updateSettings({ serviceMessages: value });
    }

    get usersSorting(): UsersSorting {
        return this._settings().usersSorting;
    }

    set usersSorting(sorting: UsersSorting) {
        this.updateSettings({ usersSorting: sorting });
    }

    get notificationsSetting(): Array<NotificationClientSettings> {
        return this._settings().notifications_v2;
    }

    set notificationsSetting(value: Array<NotificationClientSettings>) {
        this.updateSettings({ notifications_v2: value });
    }

    get watermark(): boolean {
        return this._settings().watermark;
    }

    set watermark(value: boolean) {
        this.updateSettings({ watermark: value });
    }

    get themeMode(): ThemeMode {
        return this._settings().themeMode;
    }

    set themeMode(value: ThemeMode) {
        this.updateSettings({ themeMode: value });
        this.themeChanged.emit(value);
    }

    get boostInUsersList(): boolean {
        return this._settings().boostInUsersList;
    }

    set boostInUsersList(value: boolean) {
        this.updateSettings({ boostInUsersList: value });
    }

    get showExternalImages(): boolean {
        return this._showExternalImages();
    }

    set showExternalImages(value: boolean) {
        this._showExternalImages.set(value);
    }

    get hideExternalImagesWarning(): boolean {
        return this._hideExternalImagesWarning();
    }

    set hideExternalImagesWarning(value: boolean) {
        this._hideExternalImagesWarning.set(value);
        this.writeSessionStorageBool('hide_external_images_warning', value);
    }

    /**
     * Возвращает сигнал с настройками для использования в компонентах
     */
    get settings() {
        return this._settings.asReadonly();
    }

    private updateSettings(partial: Partial<ClientSettingsModel>) {
        this._settings.update(current => ({ ...current, ...partial }));
        this.saveSettings();
    }

    private saveSettings() {
        this._saveSubject.next();
    }

    private executeSave() {
        const settings = this._settings();

        if (this._me?.registered) {
            this._remoteCommandService.sendCommand(new UserSettingsNamespace.SaveClientSettingsCommand(settings))
        } else if (isInBrowser()) {
            const rawSettings: string = JSON.stringify(settings)
            window.localStorage.setItem(UserSettingsService.KEY_SETTINGS, rawSettings);
        }
    }

    private readSessionStorageBool(key: string, defaultValue: boolean = false): boolean {
        if (!isInBrowser()) return defaultValue;
        const raw = window.sessionStorage.getItem(key)
        if (raw === null) {
            return defaultValue
        }

        return raw === 'true'
    }

    private writeSessionStorageBool(key: string, value: boolean) {
        if (!isInBrowser()) return;
        window.sessionStorage.setItem(key, value ? 'true' : 'false')
    }

    private defaultSettings(): ClientSettingsModel {
        return {
            notifications_v2: [
                NotificationClientSettings.mainRoom,
                NotificationClientSettings.personalRooms,
                NotificationClientSettings.otherRooms,
                NotificationClientSettings.answers
            ],
            pictures: false,
            serviceMessages: false,
            allowPrivateMessages: AllowPrivateMessages.all,
            usersSorting: UsersSorting.alphabetic_asc,
            watermark: true,
            themeMode: 'system',
            boostInUsersList: false,
        }
    }
}